import React from "react";
import { Button, Tag, Tooltip, Typography } from "antd";
import {
  DeleteOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  UserOutlined,
  HolderOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styles from "./style.module.scss";

const { Text } = Typography;

export interface ITripOrderCardData {
  id: number;
  number: string;
  status: string;
  statusColor?: string;
  sequence: number;
  distanceToNext: number;
  distanceFromPrevious?: number;
  client?: {
    firstName?: string;
    lastName?: string;
    mobile?: string;
  };
  clientAddress?: string;
}

interface TripOrderCardProps {
  order: ITripOrderCardData;
  index: number;
  isDragging?: boolean;
  isSelected?: boolean;
  isEditable?: boolean;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  onRemove?: (orderId: number) => void;
  onSelect?: (orderId: number) => void;
}

const TripOrderCard: React.FC<TripOrderCardProps> = ({
  order,
  index,
  isDragging = false,
  isSelected = false,
  isEditable = true,
  dragHandleProps,
  onRemove,
  onSelect,
}) => {
  const { t } = useTranslation();

  const handleClick = () => {
    onSelect?.(order.id);
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemove?.(order.id);
  };

  const clientName = order.client
    ? `${order.client.firstName || ""} ${order.client.lastName || ""}`.trim()
    : "";

  return (
    <div
      className={`${styles.orderCard} ${isDragging ? styles.dragging : ""} ${
        isSelected ? styles.selected : ""
      }`}
      onClick={handleClick}
      role="listitem"
      aria-label={`${t("order")} ${order.number}, ${t("sequence")} ${index + 1}`}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      {/* Sequence Number Badge */}
      <div className={styles.sequenceBadge}>
        <span className={styles.sequenceNumber}>{index + 1}</span>
      </div>

      {/* Drag Handle */}
      {isEditable && (
        <div className={styles.dragHandle} {...dragHandleProps}>
          <HolderOutlined />
        </div>
      )}

      {/* Order Info */}
      <div className={styles.orderInfo}>
        <div className={styles.orderHeader}>
          <Link
            to={`/orders/details/${order.id}`}
            className={styles.orderNumberLink}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            #{order.number}
          </Link>
          <Tag color={order.statusColor || "processing"}>
            {t(order.status)}
          </Tag>
        </div>

        {/* Customer Info */}
        <div className={styles.customerInfo}>
          {clientName && (
            <div className={styles.infoRow}>
              <UserOutlined />
              <Text>{clientName}</Text>
            </div>
          )}
          {order.client?.mobile && (
            <div className={styles.infoRow}>
              <PhoneOutlined />
              <Text type="secondary">{order.client.mobile}</Text>
            </div>
          )}
          {order.clientAddress && (
            <div className={styles.infoRow}>
              <EnvironmentOutlined />
              <Text
                type="secondary"
                ellipsis={{ tooltip: order.clientAddress }}
                className={styles.addressText}
              >
                {order.clientAddress}
              </Text>
            </div>
          )}
        </div>

        {/* Distance Info */}
        {(order.distanceFromPrevious !== undefined && order.distanceFromPrevious > 0) && (
          <div className={styles.distanceInfo}>
            <Text type="secondary">
              {t("distance_from_previous")}: {order.distanceFromPrevious.toFixed(2)} {t("km")}
            </Text>
          </div>
        )}
      </div>

      {/* Actions */}
      {isEditable && onRemove && (
        <div className={styles.actions}>
          <Tooltip title={t("remove_from_trip")}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemove}
              aria-label={t("remove_order")}
              size="small"
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default React.memo(TripOrderCard);
